import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, getDocs, orderBy, limit, startAfter, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';

const INITIAL_LOAD = 8;
const NEXT_BATCH_SIZE = 15;

// Global cache to persist across navigations
const feedCache = {
  campus: [],
  global: [],
  following: [],
  lastDocs: { campus: null, global: null, following: null },
  hasMore: { campus: true, global: true, following: true },
  scrolls: { campus: 0, global: 0, following: 0 },
  userProfiles: {},
  likedPosts: new Set(),
  savedPosts: new Set(),
  followingSet: new Set(),
  initialized: false,
};

export const useFeedData = () => {
  const [activeTab, setActiveTab] = useState('global');
  const [campusPosts, setCampusPosts] = useState(feedCache.campus);
  const [globalPosts, setGlobalPosts] = useState(feedCache.global);
  const [followingPosts, setFollowingPosts] = useState(feedCache.following);

  
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [userCollege, setUserCollege] = useState('');

  const [likedPosts, setLikedPosts] = useState(feedCache.likedPosts);
  const [savedPosts, setSavedPosts] = useState(feedCache.savedPosts);
  const [followingSet, setFollowingSet] = useState(feedCache.followingSet);
  const [userProfiles, setUserProfiles] = useState(feedCache.userProfiles);

  const deduplicate = (posts) => {
    const seen = new Set();
    return posts.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  };

  // 1. Initialize user metadata (College, Likes, Follows, Saves)
  useEffect(() => {
    const initUserData = async () => {
      const user = auth.currentUser;
      if (!user) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) setUserCollege(userDoc.data().college || '');

        if (!feedCache.initialized) {
          const likedSnap = await getDocs(query(collection(db, 'users', user.uid, 'likedPosts'), where('isActive', '==', true)));
          const followingSnap = await getDocs(query(collection(db, 'users', user.uid, 'following'), where('isActive', '==', true)));
          const savedSnap = await getDocs(query(collection(db, 'users', user.uid, 'savedPosts')));
          
          feedCache.likedPosts = new Set(likedSnap.docs.map(d => d.id));
          feedCache.followingSet = new Set(followingSnap.docs.map(d => d.id));
          feedCache.savedPosts = new Set(savedSnap.docs.map(d => d.id));
          feedCache.initialized = true;

          setLikedPosts(feedCache.likedPosts);
          setFollowingSet(feedCache.followingSet);
          setSavedPosts(feedCache.savedPosts);
        }
      } catch (err) {
        console.error("Failed to initialize user data", err);
      }
    };
    initUserData();
  }, []);

  const fetchMissingProfiles = async (posts) => {
    const missingUids = [...new Set(posts.map(p => p.uid))].filter(uid => !feedCache.userProfiles[uid]);
    if (missingUids.length === 0) return;

    try {
      const promises = missingUids.map(uid => getDoc(doc(db, 'users', uid)));
      const docs = await Promise.all(promises);
      const newProfiles = {};
      docs.forEach((docSnap, i) => {
        if (docSnap.exists()) {
          newProfiles[missingUids[i]] = docSnap.data();
        } else {
          newProfiles[missingUids[i]] = { name: 'Anonymous' };
        }
      });
      feedCache.userProfiles = { ...feedCache.userProfiles, ...newProfiles };
      setUserProfiles({ ...feedCache.userProfiles });
    } catch (e) {
      console.error("Failed to fetch profiles", e);
    }
  };

  const fetchFollowingFeed = async (isLoadMore = false) => {
    const user = auth.currentUser;
    if (!user) return;

    const currentFollowingPosts = feedCache.following;
    if (currentFollowingPosts.length > 0 && !isLoadMore) {
       setFollowingPosts(currentFollowingPosts);
       return;
    }

    if (feedCache.followingSet.size === 0) {
      setFollowingPosts([]);
      return;
    }

    try {
      if (isLoadMore) setLoadingMore(true);
      else setLoading(true);

      const followedUids = Array.from(feedCache.followingSet);
      // Chunking if following more than 10 people
      const chunkSize = 10;
      let allPosts = [];
      
      for (let i = 0; i < followedUids.length; i += chunkSize) {
        const chunk = followedUids.slice(i, i + chunkSize);
        let q = query(collection(db, 'sparkposts'), where('uid', 'in', chunk), orderBy('createdAt', 'desc'), limit(isLoadMore ? NEXT_BATCH_SIZE : INITIAL_LOAD));
        
        if (isLoadMore && feedCache.lastDocs.following) {
           q = query(q, startAfter(feedCache.lastDocs.following));
        }
        
        const snap = await getDocs(q);
        const posts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        allPosts = [...allPosts, ...posts];
      }

      // Filter out campus-only visibility
      allPosts = allPosts.filter(p => !p.visibility || p.visibility !== 'campus');
      allPosts.sort((a, b) => b.createdAt - a.createdAt);

      await fetchMissingProfiles(allPosts);

      const merged = deduplicate(isLoadMore ? [...feedCache.following, ...allPosts] : allPosts);
      feedCache.following = merged;
      if (allPosts.length > 0) feedCache.lastDocs.following = allPosts[allPosts.length - 1];
      if (allPosts.length < INITIAL_LOAD && !isLoadMore) feedCache.hasMore.following = false;

      setFollowingPosts(merged);
    } catch (e) {
      console.error("Following feed error", e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchFeed = useCallback(async (type, isLoadMore = false) => {
    if (type === 'following') return fetchFollowingFeed(isLoadMore);
    if (type === 'campus' && !userCollege) return;

    const cachedPosts = type === 'campus' ? feedCache.campus : feedCache.global;
    if (cachedPosts.length > 0 && !isLoadMore) {
      if (type === 'campus') setCampusPosts(cachedPosts);
      else setGlobalPosts(cachedPosts);
      return;
    }

    try {
      if (isLoadMore) setLoadingMore(true);
      else setLoading(true);

      let q;
      const baseQuery = collection(db, 'sparkposts');
      const limits = limit(isLoadMore ? NEXT_BATCH_SIZE : INITIAL_LOAD);
      const orders = orderBy('createdAt', 'desc');

      if (type === 'campus') {
        q = query(baseQuery, where('college', '==', userCollege), where('visibility', '==', 'campus'), orders, limits);
      } else {
        q = query(baseQuery, orders, limits);
      }

      if (isLoadMore && feedCache.lastDocs[type]) {
        q = query(q, startAfter(feedCache.lastDocs[type]));
      }

      const snap = await getDocs(q);
      let posts = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      if (type === 'global') {
         posts = posts.filter(p => p.visibility !== 'campus' && p.visibility !== 'followers');
      }

      await fetchMissingProfiles(posts);

      if (posts.length > 0) {
         feedCache.lastDocs[type] = snap.docs[snap.docs.length - 1];
      }
      if (snap.empty) {
         feedCache.hasMore[type] = false;
      }

      const merged = deduplicate(isLoadMore ? [...cachedPosts, ...posts] : posts);
      
      if (type === 'campus') {
        feedCache.campus = merged;
        setCampusPosts(merged);
      } else {
        feedCache.global = merged;
        setGlobalPosts(merged);
      }

    } catch (e) {
      console.error(`Error fetching ${type} feed`, e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userCollege]);

  // Handle Initial Fetch
  useEffect(() => {
    fetchFeed(activeTab);
  }, [activeTab, fetchFeed]);

  const loadMore = () => {
    if (feedCache.hasMore[activeTab] && !loadingMore) {
      fetchFeed(activeTab, true);
    }
  };

  const handleOptimisticUpdate = (postId, updateFn) => {
    const updater = (prev) => prev.map(p => p.id === postId ? updateFn(p) : p);
    setCampusPosts(updater);
    setGlobalPosts(updater);
    setFollowingPosts(updater);
    feedCache.campus = updater(feedCache.campus);
    feedCache.global = updater(feedCache.global);
    feedCache.following = updater(feedCache.following);
  };

  const handleLikeUpdate = (postId, isLiked) => {
    setLikedPosts(prev => {
      const next = new Set(prev);
      if (isLiked) next.add(postId);
      else next.delete(postId);
      feedCache.likedPosts = next;
      return next;
    });
    handleOptimisticUpdate(postId, p => ({ ...p, likes: (p.likes || 0) + (isLiked ? 1 : -1) }));
  };

  const handleFollowUpdate = (uid, isFollowing) => {
    setFollowingSet(prev => {
      const next = new Set(prev);
      if (isFollowing) next.add(uid);
      else next.delete(uid);
      feedCache.followingSet = next;
      return next;
    });
  };

  const handleSaveUpdate = (postId, isSaved) => {
    setSavedPosts(prev => {
      const next = new Set(prev);
      if (isSaved) next.add(postId);
      else next.delete(postId);
      feedCache.savedPosts = next;
      return next;
    });
  };

  const getFeedScroll = useCallback((tab) => feedCache.scrolls[tab] || 0, []);
  const setFeedScroll = useCallback((tab, val) => { feedCache.scrolls[tab] = val; }, []);

  return {
    activeTab,
    setActiveTab,
    posts: activeTab === 'campus' ? campusPosts : (activeTab === 'following' ? followingPosts : globalPosts),
    loading,
    loadingMore,
    loadMore,
    likedPosts,
    savedPosts,
    followingSet,
    userProfiles,
    handleLikeUpdate,
    handleFollowUpdate,
    handleSaveUpdate,
    handleOptimisticUpdate,
    getFeedScroll,
    setFeedScroll
  };
};
